import React, { FC, createRef } from 'react'
import styled from 'styled-components'
import { sortBy } from 'lodash'
import { Box, Dropdown, MenuItem } from '@ads/front-ds'
import { Menu, PillCombo } from '@ads/front-core/atoms'
import useDropdown from '@ads/front-core/hooks/useDropdown'
import { useClickOutside } from '@ads/front-core/hooks/useEventsOutside'
import { TDictionary } from '@ads/front-core/interfaces'
import { lng } from '@ads/front-core/locales'

interface IPillSelect {
  label: string
  selectedOption?: TDictionary['id']
  options?: TDictionary[]
  disabledClear?: boolean
  onChange: (selectedOption: TDictionary['id']) => void
}

export const PillSingleSelect: FC<IPillSelect> = ({
  label,
  options,
  selectedOption,
  disabledClear = false,
  onChange,
}) => {
  const buttonRef = createRef<HTMLDivElement>()
  const menuRef = createRef<HTMLDivElement>()

  const {
    isOpen,
    direction,
    handleToggleDropdown,
    handleCloseDropdown,
  } = useDropdown({ menuRef })

  useClickOutside([buttonRef, menuRef], handleCloseDropdown)

  const handleSelect = (id: TDictionary['id']) => {
    handleCloseDropdown()
    onChange(id)
  }

  const handleClear = () => onChange(undefined)

  const value = !selectedOption
    ? lng('common:all')
    : options?.find(o => o.id === selectedOption)?.value

  const optionsSorted = sortBy(options, 'value')

  return (
    <DropdownStyled
      isOpen={isOpen}
      direction={direction}
      menu={
        <Menu ref={menuRef} minWidth={300} maxHeight={300}>
          {optionsSorted.map(option => (
            <MenuItem
              key={`-${option.id}`}
              label={option.value}
              onClick={() => handleSelect(option.id)}
            />
          ))}
        </Menu>
      }
      toggle={
        <Box ref={buttonRef}>
          <PillCombo
            prefix={label}
            label={value}
            isOpen={isOpen}
            onClick={handleToggleDropdown}
            onClear={selectedOption && !disabledClear ? handleClear : undefined}
          />
        </Box>
      }
    />
  )
}

const DropdownStyled = styled(Dropdown)`
  align-items: center;

  & > div {
    width: 100%;
  }
`
